import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface SmoothScrollProps {
  children: ReactNode;
}

/**
 * Global reduced-motion strategy.
 *
 * Single source of truth for "should this component animate?". CSS-only
 * animations are additionally neutralised by the `@media (prefers-reduced-motion:
 * reduce)` block in `src/index.css`; this hook covers the JS-driven ones
 * (rAF loops, timers, transform tweening) that CSS cannot reach.
 */
export const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent) =>
      setPrefersReducedMotion(event.matches);

    setPrefersReducedMotion(query.matches);

    // Safari < 14 only supports the deprecated addListener API.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Detects coarse/touch-primary pointers so hover-driven decoration can be
 * skipped entirely rather than rendered and left inert.
 */
export const useIsTouchDevice = (): boolean => {
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const query = window.matchMedia('(hover: none), (pointer: coarse)');
    const onChange = (event: MediaQueryListEvent) => setIsTouch(event.matches);

    setIsTouch(query.matches);

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  return isTouch;
};

/**
 * Layout pass-through.
 *
 * This previously ran a scroll listener that wrote `scrollTop`/`scrolling` into
 * state on every animation frame. `scrollTop` was never read, and because this
 * component wraps the entire page, every one of those writes re-rendered the
 * whole tree. Its only visual output was a constant `translateY(0)` and a
 * `.smooth-scroll-container` class that has no CSS rule anywhere in the app.
 *
 * It is kept as a named export (LandingPage and FAQ both wrap their trees in
 * it) but reduced to a transparent wrapper that costs nothing.
 */
export const SmoothScroll: React.FC<SmoothScrollProps> = ({ children }) => (
  <>{children}</>
);

/**
 * Reveal-on-scroll hook.
 *
 * The previous implementation took an options object and listed it directly in
 * the effect dependency array. Callers construct that object inline
 * (`useScrollReveal({ threshold })`), so it was a fresh reference on every
 * render: the observer was torn down and rebuilt on every single render of
 * every revealed section. Options are now read through a ref and the effect
 * depends only on the primitive threshold.
 */
export const useScrollReveal = (options: IntersectionObserverInit = {}) => {
  const [revealed, setRevealed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const threshold = Array.isArray(options.threshold)
    ? options.threshold.join(',')
    : options.threshold ?? 0.1;

  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    const element = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, ...optionsRef.current },
    );

    observer.observe(element);

    return () => observer.disconnect();
    // `threshold` is the only option any caller varies; depending on the
    // options object itself would rebuild the observer on every render.
  }, [threshold]);

  return { ref, revealed };
};

/**
 * Section wrapper — reveal behaviour removed.
 *
 * This used to mount its children at `opacity-0` and fade them in over 1s once
 * an IntersectionObserver fired. That cost us three things we care about:
 * perceived render latency (content is painted but deliberately invisible),
 * crawler-visible content, and a per-section observer. The site is targeting a
 * snappy, information-dense feel, so the reveal is gone rather than merely
 * throttled or gated behind reduced-motion.
 *
 * The export and its prop signature are retained because LandingPage composes
 * with it; `delay` and `threshold` are now accepted and ignored.
 */
export const ScrollRevealSection: React.FC<{
  children: ReactNode;
  className?: string;
  delay?: number;
  threshold?: number;
}> = ({ children, className = '' }) => (
  <div className={className}>{children}</div>
);
